import { displayName } from '../../types';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Add, Assessment, Schedule } from '@mui/icons-material';
import { apiService } from '../../services/api';
import CreatePerformanceCycleModal from '../../components/CreatePerformanceCycleModal';

const Performance: React.FC = () => {
  const [cycles, setCycles] = useState<any[]>([]);
  const [reviews, setReviews] = useState<any[]>([]);
  const [probations, setProbations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isCycleModalOpen, setIsCycleModalOpen] = useState(false);

  const fetchPerformanceData = async () => {
    try {
      setLoading(true);
      const [cyclesData, reviewsData, probationsData] = await Promise.all([
        apiService.getPerformanceCycles(),
        apiService.getMyReviews(),
        apiService.getActiveProbations(),
      ]);
      setCycles(cyclesData);
      setReviews(reviewsData);
      setProbations(probationsData);
    } catch (err: any) {
      console.error('Error fetching performance data:', err);
      setError('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchPerformanceDataEffect = async () => {
      try {
        setLoading(true);
        const [cyclesData, reviewsData, probationsData] = await Promise.all([
          apiService.getPerformanceCycles(),
          apiService.getMyReviews(),
          apiService.getActiveProbations(),
        ]);
        setCycles(cyclesData);
        setReviews(reviewsData);
        setProbations(probationsData);
      } catch (err: any) {
        console.error('Error fetching performance data:', err);
        setError('Failed to load performance data');
      } finally {
        setLoading(false);
      }
    };

    fetchPerformanceDataEffect();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'finalized':
      case 'completed':
        return 'success';
      case 'active':
      case 'manager_review':
        return 'primary';
      case 'self_review':
        return 'warning';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent={{ xs: "column", sm: "space-between" }} alignItems={{ xs: "flex-start", sm: "center" }} gap={1} mb={3}>
        <Typography variant="h4">Performance Management</Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<Schedule />}
            sx={{ mr: 2 }}
            disabled
            title="Performance calendar is not yet available"
          >
            View Calendar (soon)
          </Button>
          <Button 
            variant="contained" 
            startIcon={<Add />}
            onClick={() => setIsCycleModalOpen(true)}
          >
            Create Cycle
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Active Cycles
              </Typography>
              <Typography variant="h4" color="primary">
                {cycles.filter(c => c.status === 'active').length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Pending Reviews
              </Typography>
              <Typography variant="h4" color="warning.main">
                {reviews.filter(r => !r.isSubmitted).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Active Probations
              </Typography>
              <Typography variant="h4" color="info.main">
                {probations.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Completed Reviews
              </Typography>
              <Typography variant="h4" color="success.main">
                {reviews.filter(r => r.isFinalized).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Performance Cycles */}
      <Typography variant="h6" gutterBottom>
        Performance Cycles
      </Typography>
      <Paper sx={{ mb: 4 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Cycle</TableCell>
                <TableCell>Period</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Self Review Deadline</TableCell>
                <TableCell>Manager Review Deadline</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cycles.map((cycle) => (
                <TableRow key={cycle.id} hover>
                  <TableCell>
                    <Typography variant="body1">{cycle.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {cycle.year}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {new Date(cycle.periodStartDate).toLocaleDateString()} - {' '}
                    {new Date(cycle.periodEndDate).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={cycle.status.replace('_', ' ').toUpperCase()}
                      color={getStatusColor(cycle.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(cycle.selfReviewDeadline).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    {new Date(cycle.managerReviewDeadline).toLocaleDateString()}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <LinearProgress variant="determinate" value={65} />
                      <Typography variant="caption">65% Complete</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      startIcon={<Assessment />}
                      disabled
                      title="Cycle detail view is not yet available"
                    >
                      View (soon)
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {cycles.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary">
                      No performance cycles found. Create your first cycle to begin performance management.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* My Reviews */}
      <Typography variant="h6" gutterBottom>
        My Performance Reviews
      </Typography>
      <Paper sx={{ mb: 4 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Cycle</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Overall Rating</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reviews.map((review) => (
                <TableRow key={review.id} hover>
                  <TableCell>{review.performanceCycle?.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={review.reviewerRole.replace('_', ' ').toUpperCase()}
                      variant="outlined"
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {review.overallRating ? (
                      <Chip
                        label={review.overallRating.replace('_', ' ')}
                        color={review.overallRating.includes('exceeds') ? 'success' : 'default'}
                        size="small"
                      />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={review.isSubmitted ? 'SUBMITTED' : 'PENDING'}
                      color={review.isSubmitted ? 'success' : 'warning'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {review.submittedAt 
                      ? new Date(review.submittedAt).toLocaleDateString()
                      : '-'
                    }
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      disabled
                      title="Review view/complete is not yet available"
                    >
                      {review.isSubmitted ? 'View' : 'Complete'} (soon)
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {reviews.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary">
                      No reviews assigned to you yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Active Probations */}
      {probations.length > 0 && (
        <>
          <Typography variant="h6" gutterBottom>
            Active Probation Periods
          </Typography>
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell>End Date</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Days Remaining</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {probations.map((probation) => (
                    <TableRow key={probation.id} hover>
                      <TableCell>{displayName(probation.employee)}</TableCell>
                      <TableCell>
                        {new Date(probation.startDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        {new Date(probation.endDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={probation.status.replace('_', ' ').toUpperCase()}
                          color={getStatusColor(probation.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {Math.ceil(
                          (new Date(probation.endDate).getTime() - new Date().getTime()) 
                          / (1000 * 60 * 60 * 24)
                        )} days
                      </TableCell>
                      <TableCell>
                        <Button
                          size="small"
                          disabled
                          title="Probation review action is not yet available"
                        >
                          Review (soon)
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      <CreatePerformanceCycleModal
        open={isCycleModalOpen}
        onClose={() => setIsCycleModalOpen(false)}
        onCycleCreated={() => {
          fetchPerformanceData();
        }}
      />
    </Box>
  );
};

export default Performance;